import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { EmployeeAccessLevel } from 'src/app/domain/organization/dto/Employee';
import { firstValueFrom, Subject } from 'rxjs';
import { filter, takeUntil, tap } from 'rxjs/operators';
import { NOTIFICATIONS, NotificationsService } from 'src/app/core/service/notifications.service';
import { ModalButtons, SpinnerProps } from 'src/app/domain/shared/component/modal-container/modal-container.component';
import { Organization } from '../../../dto/Organization';
import { EmployeeService } from '../../../service/employee.service';
import { EmployeeCrudDTO } from 'src/app/domain/company/dto/EmployeeCRUDDTO';
import { AuthService, UserInfo } from 'src/app/domain/auth/service/auth.service';
import { EmployeeRole } from '../team-table/team-table.component';
import { OrganizationAccesses } from '../../service/organizations.service';
import { CookieService } from 'ngx-cookie-service';
import { CURRENT_ROLES, hasAdminPDFAccess, hasAdminPortalAccess, hasMarketAnalysisAccess, hasDataRoomAccess } from "../../../../recovery/util/employee-recovery.util";
import { RegExpService } from "../../../../../util/common/reg-exp.service";

@Component({
  selector: 'app-team-modal-crud',
  templateUrl: './team-modal-crud.component.html',
  styleUrls: ['./team-modal-crud.component.scss'],
})
export class TeamModalCrudComponent implements OnChanges, OnInit, OnDestroy {
  private _destroyStream: Subject<void> = new Subject<void>();

  @Input() selectedOrg: Organization;
  @Input() orgAccesses: OrganizationAccesses;
  @Input() user: EmployeeCrudDTO;
  @Input() isKpi = false;
  @Input() companyName: string;
  @Output() closeEmitter: EventEmitter<boolean> = new EventEmitter<boolean>(); // true if crud operation was successful.

  readonly regexpService = RegExpService;

  showDeleteEmployeeModal = false;
  deleteMemberTextList: string[];
  userInfo: UserInfo;

  teamMemberForm: FormGroup<{
    name: FormControl<string>;
    companyName: FormControl<string>;
    mail: FormControl<string>;
    accessLevel: FormControl<EmployeeAccessLevel>;
    kpiAccessLevel: FormControl<EmployeeAccessLevel>;
    financialStatementAccessLevel: FormControl<EmployeeAccessLevel>;
    family: FormControl<string>;
    position: FormControl<string>;
    phone: FormControl<string>;
    portalPdf: FormControl<boolean>;
    marketAnalysis: FormControl<boolean>;
    kpis: FormControl<boolean>;
    kpiNotification: FormControl<boolean>;
    portfolioNotification: FormControl<boolean>;
    portalAdminAccess: FormControl<boolean>;
    dataRoomAccess: FormControl<boolean>;
    id: FormControl<number>;
    companyId: FormControl<string>;
    accessStatus: FormControl<string>;
    uuid: FormControl<string>;
    idCompany: FormControl<number>;
    lastNotification: FormControl<Date>;
    organizationAdmin: FormControl<boolean>;
    orgId: FormControl<number>;
  }>;

  modalButtons: ModalButtons = {
    dismiss: 'Cancel',
    apply: 'Apply',
  };

  spinnerProps: SpinnerProps = {
    isSpinnerActive: false,
    notActiveOnApply: true,
  };

  currentRoles = CURRENT_ROLES;
  adminOrViewerOrNoAccessRoles: EmployeeRole[] = [CURRENT_ROLES.Admin, CURRENT_ROLES.Viewer, CURRENT_ROLES['No Access']];
  adminOrNoAccessRoles: EmployeeRole[] = [CURRENT_ROLES.Admin, CURRENT_ROLES['No Access']];
  viewerOrNoAccessRoles: EmployeeRole[] = [CURRENT_ROLES.Viewer, CURRENT_ROLES['No Access']];

  hasPortalAccess = hasAdminPortalAccess;
  hasPDFAccess = hasAdminPDFAccess;
  hasMarketAnalysisAccess = hasMarketAnalysisAccess;
  hasDataRoomAccess = hasDataRoomAccess;

  positions = POSITION_ROLES;

  constructor(
    private notificationService: NotificationsService,
    private authService: AuthService,
    private fb: FormBuilder,
    private employeeService: EmployeeService,
    private cookieService: CookieService,
  ) {
    this.authService.userInfo
      .pipe(
        filter((userInfo: UserInfo): boolean => Boolean(userInfo)),
        tap((userInfo: UserInfo): UserInfo => this.userInfo = userInfo),
        takeUntil(this._destroyStream)
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.teamMemberForm.controls.accessLevel
      .valueChanges
      .pipe(takeUntil(this._destroyStream))
      .subscribe((newAccessLevel) => {
        this.toggleFmsAccess(newAccessLevel);
      });
    this.initData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.user) {
      this.initData();
    }
    if (this.user?.id && !this.checkUserDeletePossibility()) {
      this.modalButtons.lowerLeft = this.isKpi ? null : 'Delete';
      this.modalButtons.lowerLeftIcon = this.isKpi ? null : 'delete.svg';
      this.modalButtons.lowerLeftClass = this.isKpi ? null : 'light-red';
      this.modalButtons.upperRight1 = this.isKpi ? null : 'Re-Invite';
    }
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  /**
   * By turning on org admin:
   * 0) access is ignored if the org does not have such service (e.x. portal admin). Otherwise:
   * 1) all access + notification fields are selected.
   * 2) all access fields are disabled, so user cannot change them.
   *
   * @param isOrgAdmin indicates if the next value is the organization's admin.
   * @param isCalledOnStart tells if this function is called from OnInit or OnChange.
   * We don't want to toggle the notifications after modal open, because it will show the wrong state.
   */
  toggleOrgAdmin(isOrgAdmin: boolean, isCalledOnStart: boolean): void {
    if (isOrgAdmin) {
      this.teamMemberForm.patchValue({ accessLevel: this.selectedOrg.fmsAccess ? 'Admin' : 'No Access' });
      this.teamMemberForm.controls.accessLevel.disable();
      if (!isCalledOnStart) {
        this.teamMemberForm.patchValue({ portfolioNotification: this.selectedOrg.fmsAccess });
        this.teamMemberForm.patchValue({ kpiNotification: this.selectedOrg.fmsAccess });
      }
      this.teamMemberForm.patchValue({ portalAdminAccess: this.selectedOrg.portalAccess });
      this.teamMemberForm.controls.portalAdminAccess.disable();
      this.teamMemberForm.patchValue({ marketAnalysis: this.selectedOrg.marketAnalysisAccess });
      this.teamMemberForm.controls.marketAnalysis.disable();
      this.teamMemberForm.patchValue({ kpiAccessLevel: this.selectedOrg.kpiAccess ? 'Admin' : 'No Access' });
      this.teamMemberForm.controls.kpiAccessLevel.disable();
      this.teamMemberForm.patchValue({ financialStatementAccessLevel: this.selectedOrg.financialStatementAccess ? 'Admin' : 'No Access' });
      this.teamMemberForm.controls.financialStatementAccessLevel.disable();
      this.teamMemberForm.patchValue({ portalPdf: this.selectedOrg.pdfAccess });
      this.teamMemberForm.controls.portalPdf.disable();
      this.teamMemberForm.patchValue({ dataRoomAccess: this.selectedOrg.dataRoomAccess });
      this.teamMemberForm.controls.dataRoomAccess.disable();
      if (this.userInfo.mail === this.user.mail) {
        this.teamMemberForm.controls.organizationAdmin.disable();
      }
    } else {
      if (this.selectedOrg.fmsAccess) {
        this.teamMemberForm.controls.kpis.enable();
        this.teamMemberForm.controls.kpiNotification.enable();
        this.teamMemberForm.controls.portfolioNotification.enable();
        this.teamMemberForm.controls.accessLevel.enable();
      }
      if (this.selectedOrg.portalAccess) {
        this.teamMemberForm.controls.portalAdminAccess.enable();
      }
      if (this.selectedOrg.marketAnalysisAccess) {
        this.teamMemberForm.controls.marketAnalysis.enable();
      }
      if (this.selectedOrg.pdfAccess) {
        this.teamMemberForm.controls.portalPdf.enable();
      }
      if (this.selectedOrg.kpiAccess) {
        this.teamMemberForm.controls.kpiAccessLevel.enable();
      }
      if (this.selectedOrg.financialStatementAccess) {
        this.teamMemberForm.controls.financialStatementAccessLevel.enable();
      }
      if (this.selectedOrg.dataRoomAccess) {
        this.teamMemberForm.controls.dataRoomAccess.enable();
      }
    }
  }

  async confirm(): Promise<void> {
    const form = this.teamMemberForm.getRawValue();
    this.teamMemberForm.patchValue({
      name: this.capitalizeFirstLetter(form.name),
      family: this.capitalizeFirstLetter(form.family),
      idCompany: this.isKpi ? form.idCompany : null,
      accessLevel: form.accessLevel ?? 'No Access',
    });

    if (this.user?.id) {
      await this.updateTeamMember();
    } else {
      await this.createTeamMember();
    }
  }

  // Emits Employee in case needed and closes the modal.
  close(isSuccessful: boolean): void {
    this.closeEmitter.emit(isSuccessful);
  }

  updatePortalAccess(access: EmployeeAccessLevel): void {
    this.teamMemberForm.patchValue({
      portalAdminAccess: this.hasPortalAccess(access as AdminPortalRole),
      portalPdf: this.hasPortalAccess(access as AdminPortalRole),
    });
  }

  async deleteTeamMember(withUpdate: boolean): Promise<void> {
    if (withUpdate) {
      await this.employeeService.delete(this.teamMemberForm.value.uuid);
    }
    this.closeEmitter.emit();
  }

  deleteButtonClicked(): void {
    if (this.user.mail === this.userInfo.mail && this.orgAccesses.isOrganizationAdmin) {
      this.notificationService.warning({ title: 'Delete Team Member', message: 'You cannot delete yourself.' });
      return;
    }
    this.showDeleteEmployeeModal = true;
  }

  async resendInvite(): Promise<void> {
    await this.employeeService.reInviteUser(this.user);
    this.notificationService.success(NOTIFICATIONS.INVITE_SENT_SUCCESS);
  }

  private initData(): void {
    this.initForm();
    this.toggleFmsAccess(this.teamMemberForm.value.accessLevel);
    this.checkDefaultAccesses();
    this.checkOrgAdmin();
    this.deleteMemberTextList = [this.user?.name + ' will be deleted'];
  }

  private initForm(): void {
    this.teamMemberForm = this.fb.group({
      name: new FormControl(this.user?.name, { validators: [Validators.required, Validators.pattern('\\s*\\S.*')] }),
      companyName: new FormControl((this.user && this.user.uuid) ? this.user.companyName : this.companyName),
      mail: new FormControl({ value: this.user?.mail, disabled: Boolean(this.user?.id) },
        { validators: [Validators.required, Validators.email] }),
      accessLevel: new FormControl<EmployeeAccessLevel>(this.user ? this.user.accessLevel : 'No Access'),
      kpiAccessLevel: new FormControl<EmployeeAccessLevel>(
        {
          value: this.user ? this.user.kpiAccessLevel : 'No Access',
          disabled: !this.selectedOrg.kpiAccess,
        },
      ),
      financialStatementAccessLevel: new FormControl<EmployeeAccessLevel>(
        {
          value: this.user ? this.user.financialStatementAccessLevel : 'No Access',
          disabled: !this.selectedOrg.financialStatementAccess,
        },
      ),
      family: new FormControl(this.user?.family),
      position: new FormControl(this.user?.position),
      phone: new FormControl(this.user?.phone, { validators: [Validators.pattern(this.regexpService.phoneOrEmpty)] }),
      portalPdf: new FormControl({
        value: Boolean(this.isKpi ? false : this.user?.portalPdf),
        disabled: !this.selectedOrg.pdfAccess,
      }),
      marketAnalysis: new FormControl({
        value: Boolean(this.isKpi ? false : this.user?.marketAnalysis),
        disabled: !this.selectedOrg.marketAnalysisAccess,
      }),
      kpis: new FormControl(Boolean(this.isKpi ? false : this.user?.kpis)),
      portfolioNotification: new FormControl(Boolean(this.isKpi ? false : this.user?.portfolioNotification)),
      portalAdminAccess: new FormControl(Boolean(this.isKpi ? false : this.user?.portalAdminAccess)),
      dataRoomAccess: new FormControl(Boolean(this.isKpi ? false : this.user?.dataRoomAccess)),
      kpiNotification: new FormControl(Boolean(this.isKpi ? false : this.user?.kpiNotification)),
      id: new FormControl(this.user?.id),
      companyId: new FormControl(this.user?.companyName),
      accessStatus: new FormControl(this.user?.accessStatus),
      uuid: new FormControl(this.user?.uuid),
      idCompany: new FormControl(this.user?.idCompany),
      lastNotification: new FormControl(this.user?.lastNotification),
      organizationAdmin: new FormControl(this.isKpi ? null : this.user?.organizationAdmin),
      orgId: new FormControl(this.isKpi ? null : this.selectedOrg.id),
    });
  }

  private toggleFmsAccess(v: EmployeeAccessLevel): void {
    if (v === 'No Access') {
      this.teamMemberForm.controls.kpis.disable();
      this.teamMemberForm.patchValue({ kpis: false });
      this.teamMemberForm.controls.kpiNotification.disable();
      this.teamMemberForm.patchValue({ kpiNotification: false });
      this.teamMemberForm.controls.portfolioNotification.disable();
      this.teamMemberForm.patchValue({ portfolioNotification: false });
    } else {
      this.selectedOrg.kpiAccess ? this.teamMemberForm.controls.kpis.enable() : this.teamMemberForm.controls.kpis.disable();
      this.selectedOrg.fmsAccess ? this.teamMemberForm.controls.portfolioNotification.enable()
        : this.teamMemberForm.controls.portfolioNotification.disable();
      this.selectedOrg.fmsAccess ? this.teamMemberForm.controls.kpiNotification.enable()
        : this.teamMemberForm.controls.kpiNotification.disable();
    }
  }

  /**
   * Restrict users from deleting themselves, except for those with the ADMINSUPER role.
   */
  private checkUserDeletePossibility(): boolean {
    return this.cookieService.get('user_info').includes(this.user.mail) && !this.authService.isAdminSuper;
  }

  /**
   * By init, sets the correct disabled states of the accesses
   * based on org purchased services (if org bought a software).
   */
  private checkDefaultAccesses(): void {
    // kpi
    if (this.selectedOrg.kpiAccess) {
      this.teamMemberForm.controls.kpis.enable();
    } else {
      this.teamMemberForm.controls.kpis.disable();
    }
    // fs
    if (this.selectedOrg.financialStatementAccess) {
      this.teamMemberForm.controls.financialStatementAccessLevel.enable();
    } else {
      this.teamMemberForm.controls.financialStatementAccessLevel.disable();
    }
    // fms
    if (this.selectedOrg.fmsAccess) {
      this.teamMemberForm.controls.accessLevel.enable();
      this.teamMemberForm.controls.portfolioNotification.enable();
      this.teamMemberForm.controls.kpiNotification.enable();
    } else {
      this.teamMemberForm.controls.accessLevel.disable();
      this.teamMemberForm.controls.portfolioNotification.disable();
      this.teamMemberForm.controls.kpiNotification.disable();
    }
    // portal
    if (this.selectedOrg.portalAccess) {
      this.teamMemberForm.controls.portalAdminAccess.enable();
    } else {
      this.teamMemberForm.controls.portalAdminAccess.disable();
    }
    // market anal
    if (this.selectedOrg.marketAnalysisAccess) {
      this.teamMemberForm.controls.marketAnalysis.enable();
    } else {
      this.teamMemberForm.controls.marketAnalysis.disable();
    }
    // pdf
    if (this.selectedOrg.pdfAccess) {
      this.teamMemberForm.controls.portalPdf.enable();
    } else {
      this.teamMemberForm.controls.portalPdf.disable();
    }
    // data room
    if (this.selectedOrg.dataRoomAccess) {
      this.teamMemberForm.controls.dataRoomAccess.enable();
    } else {
      this.teamMemberForm.controls.dataRoomAccess.disable();
    }
  }

  private checkOrgAdmin(): void {
    if (this.teamMemberForm.getRawValue().organizationAdmin) {
      this.toggleOrgAdmin(true, true);
    }
  }

  private async updateTeamMember(): Promise<void> {
    await this.employeeService.update(this.teamMemberForm.getRawValue(), this.isKpi);
    this.close(true);
  }

  private async createTeamMember(): Promise<void> {
    try {
      await firstValueFrom(this.employeeService.create(this.teamMemberForm.getRawValue(), this.isKpi)
        .pipe(takeUntil(this._destroyStream)));
      this.checkMailNotification();
      this.close(true);
    } catch (error) {
      if (error.status === 409 || error.includes('409')) {
        this.teamMemberForm.controls.mail.setErrors({ conflict: true });
      }
    }
  }

  private capitalizeFirstLetter(str: string): string {
    if (!str?.length) {
      return str;
    }
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  private checkMailNotification(): void {
    if (this.user?.accessLevel !== 'No Access' && this.teamMemberForm.value.accessLevel !== 'No Access') {
      this.notificationService.info(
        {
          title: 'Email sent',
          message: 'An email with invitation was sent to: ' + this.teamMemberForm.getRawValue().mail,
        });
    }
  }
}

export const POSITION_ROLES = [
  'General Partner',
  'Managing Partner',
  'Compliance',
  'Investor Relations',
  'Partnership Bookkeeper',
  'Management Company Bookkeeper',
  'Controller',
  'Auditor',
  'Auditor Partner',
  'Tax Auditor',
  'Tax Auditor Partner',
  'Portfolio Manager',
  'Associate',
  'Principle',
  'CEO',
  'COO',
  'CFO',
];

export const ICON_FOR_USER_ROLE = {
  Admin: 'admin-icon.svg; padding-right-0p1',
  Viewer: 'user-icon.svg; padding-right-0p1',
  'No Access': '',
};

export type AdminPortalRole = 'No Access' | 'Admin';
export type AdminPDFAccess = 'No Access' | 'Admin';
export type MarketAnalysisAccess = 'No Access' | 'Viewer';
export type DataRoomAccess = 'No Access' | 'Admin';
